import { TableActionsWrapper, TableBadgeLabel } from '@components'

import {
  Button,
  Divider,
  Flex,
  FormItem,
  FormItemsGroup,
  Input,
  Modal,
  ModalProps,
  Spacing,
  Table,
  Text,
  useModal,
  withModalConditionalRender,
} from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { ModalId } from '../../../../enums/modalId'
import { DeleteProductUnitsConfirmationModal } from './elements/DeleteProductUnitsConfirmationModal'
import { useProductUnitsListTableColumns } from './hooks/useProductUnitsListTableColumns'
import { useProductUnitsModal } from './hooks/useProductUnitsModal'

export type ProductUnitOnSaveParams = {
  units: string[]
  lastUnitAdded: string | undefined
}

export interface ProductUnitsModalProps extends ModalProps {
  onClose: () => void
  onSave: (params: ProductUnitOnSaveParams) => void
  units: string[]
}

const ProductUnitsModalComponent = ({
  onClose,
  onSave,
  units: baseUnits,
  ...rest
}: ProductUnitsModalProps): ReactElement => {
  const { t } = useTranslation()
  const { close: closeDeleteModal, open: openDeleteModal } = useModal(ModalId.DeleteProductUnitsConfirmationModal)

  const {
    handleAddUnitClick,
    handleUnitInputChange,
    setUnitToDelete,
    setUnits,
    lastUnitAdded,
    unitToAdd,
    unitToDelete,
    units,
  } = useProductUnitsModal({ baseUnits })

  const handleRowDeleteActionClick = useCallback(
    (id: string) => () => {
      setUnitToDelete(id)
      openDeleteModal()
    },
    [openDeleteModal, setUnitToDelete],
  )

  const columns = useProductUnitsListTableColumns({ handleRowDeleteActionClick, t })

  const handleSaveClick = useCallback(() => {
    onSave({
      units: units.map((unit) => unit.id),
      lastUnitAdded,
    })
  }, [lastUnitAdded, units, onSave])

  const handleModalConfirmDelete = useCallback(() => {
    setUnits((units) => units.filter((unit) => unit.id !== unitToDelete))
    setUnitToDelete(undefined)
    closeDeleteModal()
  }, [closeDeleteModal, unitToDelete, setUnitToDelete, setUnits])

  const handleModalClose = useCallback(() => {
    setUnitToDelete(undefined)
    closeDeleteModal()
  }, [closeDeleteModal, setUnitToDelete])

  return (
    <>
      <Modal size="s" {...rest}>
        <Modal.Header title={t('products.product_units_modal.title')} />

        <Modal.Body>
          <FormItemsGroup itemsInRow={1} spacing={Spacing.S16}>
            <Text variant="h4">{t('products.product_units_modal.form.header')}</Text>

            <FormItemsGroup itemsInRow={4} columnSpacing={Spacing.S8}>
              <FormItem
                itemColumnStart={1}
                itemColumnEnd={4}
                label={t('products.product_units_modal.form.unit')}
                name="unit"
              >
                <Input onChange={handleUnitInputChange} value={unitToAdd} />
              </FormItem>

              <Flex alignItems="flex-end">
                <Button
                  variant="outlined"
                  icon="circlePlus"
                  fullWidth
                  onClick={handleAddUnitClick}
                  disabled={unitToAdd === ''}
                >
                  {t('products.product_units_modal.form.actions.add')}
                </Button>
              </Flex>
            </FormItemsGroup>

            <Divider />

            <TableActionsWrapper>
              <TableBadgeLabel amount={units.length} color="neutral">
                {t('products.product_units_modal.table.header')}
              </TableBadgeLabel>
            </TableActionsWrapper>

            <Table columns={columns} data={units} withSkeletonView />
          </FormItemsGroup>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="text" onClick={onClose}>
            {t('products.product_units_modal.actions.cancel')}
          </Button>

          <Button onClick={handleSaveClick}>{t('products.product_units_modal.actions.save')}</Button>
        </Modal.Footer>
      </Modal>

      <DeleteProductUnitsConfirmationModal
        unit={unitToDelete}
        onClose={handleModalClose}
        onDelete={handleModalConfirmDelete}
      />
    </>
  )
}

export const ProductUnitsModal = withModalConditionalRender(ProductUnitsModalComponent)
